// src/components/Settings.jsx
import { useState, useCallback } from "react";
import DeleteAccountModal from "./DeleteAccountModal";
import { supabase, hasSupabase as _hasSupabase } from "../lib/supabaseClient";

const BENCHMARKS = ["SPY", "QQQ", "DIA", "IWM", "VTI", "ACWI"];
const TABS_LIST  = [
  { id: "builder",     label: "Portfolio" },
  { id: "valuation",   label: "Valuation" },
  { id: "optimizer",   label: "Optimizer" },
  { id: "charts",      label: "Analytics" },
  { id: "marketwatch", label: "Market Watch" },
  { id: "screener",    label: "Screener" },
  { id: "macro",       label: "Macro" },
];
const POLL_OPTIONS = [
  { ms: 1000,  label: "1s  (high)" },
  { ms: 2000,  label: "2s  (default)" },
  { ms: 5000,  label: "5s  (balanced)" },
  { ms: 10000, label: "10s (low)" },
  { ms: 0,     label: "Off" },
];

const TABS_NAV = [
  { id: "general",    label: "GENERAL"    },
  { id: "appearance", label: "APPEARANCE" },
  { id: "alerts",     label: "ALERTS"     },
  { id: "account",    label: "ACCOUNT"    },
];

const ACCENT_PRESETS = [
  { label: "Signal Green",  hex: "#3ddc84" },
  { label: "Electric Blue", hex: "#38bdf8" },
  { label: "Solar Gold",    hex: "#f0a500" },
  { label: "Rose Red",      hex: "#ff5c5c" },
  { label: "Violet",        hex: "#a78bfa" },
];

const ALERT_ROWS = [
  { key: "earnings", label: "Earnings Alerts", hint: "Notify before earnings reports"      },
  { key: "geoRisk",  label: "Geo-Risk Alerts", hint: "Flag elevated geopolitical risk"     },
  { key: "price",    label: "Price Alerts",    hint: "Significant price moves"             },
  { key: "macro",    label: "Macro Alerts",    hint: "Major economic data releases"        },
  { key: "weekly",   label: "Weekly Summary",  hint: "Weekly portfolio digest"             },
];

// ── Shared sub-components ─────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        color: "var(--text-secondary)", fontSize: 10, letterSpacing: "0.12em",
        marginBottom: 12, borderBottom: "1px solid var(--border)", paddingBottom: 6,
      }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: "var(--text-primary)", fontSize: 12 }}>{label}</div>
        {hint && <div style={{ color: "var(--text-secondary)", fontSize: 10, marginTop: 2 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
        background: value ? "var(--accent)" : "var(--surface-3)",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute", top: 3, left: value ? 21 : 3,
        width: 16, height: 16, borderRadius: "50%",
        background: value ? "var(--bg)" : "var(--text-tertiary)",
        transition: "left 0.2s",
      }} />
    </button>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
        color: "var(--text-primary)", fontSize: 11, padding: "4px 8px", cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      {options.map(o => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  );
}

function NumberInput({ value, onChange, min, max, step = 0.1, unit = "" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <input
        type="number"
        value={value}
        min={min} max={max} step={step}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          width: 64, background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)", color: "var(--accent)", fontSize: 12,
          padding: "4px 6px", textAlign: "right", fontFamily: "inherit",
        }}
      />
      {unit && <span style={{ color: "var(--text-secondary)", fontSize: 11 }}>{unit}</span>}
    </div>
  );
}

// ── Main Settings component ───────────────────────────────────────────────────
export default function Settings({ settings, setSetting, resetDefaults, holdings, hasSupabase, onSignOut, onClearData, user, tier }) {
  const [activeTab,          setActiveTab]          = useState("general");
  const [showDeleteConfirm,  setShowDeleteConfirm]  = useState(false);
  const [showDeleteModal,    setShowDeleteModal]    = useState(false);
  const [displayName,        setDisplayName]        = useState(settings.displayName ?? "");
  const [nameSaved,          setNameSaved]          = useState(false);
  const [nameError,          setNameError]          = useState("");

  const holdingsCount = holdings?.length ?? 0;
  // user.id is already available from the auth prop — no localStorage needed
  const userId = user?.id ?? "—";

  const saveDisplayName = useCallback(() => {
    const trimmed = displayName.trim();
    if (!trimmed) { setNameError("Name cannot be empty."); return; }
    setNameError("");
    setNameSaved(false);
    // setSetting already writes displayName to profiles.display_name via upsertUserPreferences
    setSetting("displayName", trimmed);
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2500);
  }, [displayName, setSetting]);

  const handleDeleteAccount = useCallback(async () => {
    await onClearData?.();
    ["holdings", "riskTolerance", "mw_watchlist", "portfolio_os_settings",
     "portfolio_os_user_id", "onboarding_complete"].forEach(k => {
      try { localStorage.removeItem(k); } catch {}
    });
    await onSignOut?.();
  }, [onClearData, onSignOut]);

  const alerts    = settings.alerts ?? {};
  const setAlert  = (key, val) => setSetting("alerts", { ...alerts, [key]: val });
  const setAccent = (hex) => {
    setSetting("accentColor", hex);
    document.documentElement.style.setProperty("--accent", hex);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, fontFamily: "var(--font-mono)" }}>

      {/* ── Tab bar ──────────────────────────────────────── */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)", flexShrink: 0, overflowX: "auto" }}>
        {TABS_NAV.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background:    "transparent",
              border:        "none",
              borderBottom:  activeTab === tab.id ? "2px solid var(--accent)" : "2px solid transparent",
              color:         activeTab === tab.id ? "var(--text-primary)" : "var(--text-secondary)",
              fontSize:      10,
              letterSpacing: "0.1em",
              padding:       "10px 16px",
              cursor:        "pointer",
              fontFamily:    "inherit",
              whiteSpace:    "nowrap",
              transition:    "color 0.15s",
              flexShrink:    0,
            }}
            onMouseEnter={e => { if (activeTab !== tab.id) e.currentTarget.style.color = "var(--text-primary)"; }}
            onMouseLeave={e => { if (activeTab !== tab.id) e.currentTarget.style.color = "var(--text-secondary)"; }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Scrollable content ───────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

        {/* GENERAL TAB */}
        {activeTab === "general" && (
          <div>
            <Section title="GENERAL">
              <Row label="Quote Poll Interval" hint="How often live prices refresh">
                <Select
                  value={settings.pollIntervalMs}
                  onChange={v => setSetting("pollIntervalMs", Number(v))}
                  options={POLL_OPTIONS.map(o => ({ value: o.ms, label: o.label }))}
                />
              </Row>
              <Row label="Hide Live Data Banner" hint="Collapse the top status bar">
                <Toggle value={settings.hideBanner} onChange={v => setSetting("hideBanner", v)} />
              </Row>
              <Row label="Compact Table Rows" hint="Tighter spacing in screener / builder">
                <Toggle value={settings.compactRows} onChange={v => setSetting("compactRows", v)} />
              </Row>
              <Row label="UI Mode" hint="Standard tabs or terminal multi-window mode">
                <Select
                  value={settings.uiMode ?? "standard"}
                  onChange={v => setSetting("uiMode", v)}
                  options={[
                    { value: "standard", label: "Standard" },
                    { value: "terminal", label: "Terminal" },
                  ]}
                />
              </Row>
              <Row label="Show Module Descriptions" hint="Header bar with description on each tab (Phase 7)">
                <Toggle
                  value={settings.showModuleDescriptions ?? true}
                  onChange={v => setSetting("showModuleDescriptions", v)}
                />
              </Row>
              <Row label="Language Mode" hint="Finance Mode uses standard terminology. Plain English replaces jargon with simple explanations.">
                <Select
                  value={settings.languageMode ?? "finance"}
                  onChange={v => setSetting("languageMode", v)}
                  options={[
                    { value: "finance", label: "Finance Mode" },
                    { value: "plain",   label: "Plain English" },
                  ]}
                />
              </Row>
              <Row label="Total Portfolio Value ($)" hint="Your real portfolio size — used to show dollar amounts on Dashboard">
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 50000"
                  value={settings.portfolioValue ?? ""}
                  onChange={e => {
                    const v = e.target.value;
                    setSetting("portfolioValue", v === "" ? null : parseFloat(v));
                  }}
                  style={{
                    background: "var(--surface)", border: "1px solid var(--border)",
                    color: "var(--text-primary)", borderRadius: 4, padding: "5px 8px",
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 11,
                    width: 140,
                  }}
                />
              </Row>
            </Section>

          </div>
        )}

        {/* APPEARANCE TAB */}
        {activeTab === "appearance" && (
          <div>
            <Section title="ACCENT COLOR">
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                {ACCENT_PRESETS.map(({ label, hex }) => {
                  const active = settings.accentColor === hex;
                  return (
                    <div key={hex} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                      <button
                        onClick={() => setAccent(hex)}
                        title={label}
                        style={{
                          width:         32, height: 32, borderRadius: "50%",
                          background:    hex,
                          border:        active ? "2px solid var(--text-primary)" : "2px solid transparent",
                          outline:       active ? "2px solid var(--surface-3)" : "none",
                          outlineOffset: 2,
                          cursor:        "pointer",
                          display:       "flex", alignItems: "center", justifyContent: "center",
                          transition:    "border-color 0.15s",
                          flexShrink:    0,
                        }}
                      >
                        {active && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="rgba(0,0,0,0.75)"
                              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                      <span style={{ fontSize: 9, color: "var(--text-secondary)", letterSpacing: "0.05em" }}>
                        {label.split(" ")[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="DISPLAY">
              <Row label="Font Size" hint="Base font size across the app (stored; visual effect in future phase)">
                <div style={{ display: "flex", gap: 2 }}>
                  {["S", "M", "L"].map(size => (
                    <button
                      key={size}
                      onClick={() => {
                        setSetting("fontSize", size);
                        const px = { S: "11px", M: "12px", L: "13px" }[size];
                        document.documentElement.style.setProperty("--font-size-base", px);
                      }}
                      style={{
                        width:        32, height: 26,
                        background:   settings.fontSize === size ? "var(--accent-dim)" : "transparent",
                        border:       settings.fontSize === size ? "1px solid var(--accent)" : "1px solid var(--border)",
                        borderRadius: "var(--radius-sm)",
                        color:        settings.fontSize === size ? "var(--accent)" : "var(--text-secondary)",
                        fontSize:     10, cursor: "pointer", fontFamily: "inherit",
                        transition:   "all 0.15s",
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </Row>

              <Row label="Number Format" hint="How large numbers are displayed (stored; visual effect in future phase)">
                <div style={{ display: "flex", gap: 2 }}>
                  {[{ value: "abbr", label: "1K" }, { value: "full", label: "1,000" }].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setSetting("numberFormat", value)}
                      style={{
                        padding:      "0 12px", height: 26,
                        background:   settings.numberFormat === value ? "var(--accent-dim)" : "transparent",
                        border:       settings.numberFormat === value ? "1px solid var(--accent)" : "1px solid var(--border)",
                        borderRadius: "var(--radius-sm)",
                        color:        settings.numberFormat === value ? "var(--accent)" : "var(--text-secondary)",
                        fontSize:     10, cursor: "pointer", fontFamily: "inherit",
                        transition:   "all 0.15s",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Row>
            </Section>
          </div>
        )}

        {/* ALERTS TAB */}
        {activeTab === "alerts" && (
          <div>
            <div style={{
              fontSize: 10, color: "var(--text-secondary)", marginBottom: 20,
              padding: "10px 12px", background: "var(--surface-2)",
              borderRadius: "var(--radius-sm)", lineHeight: 1.7,
            }}>
              Alert delivery is not yet implemented — these preferences are saved for the upcoming notifications feature.
            </div>
            <Section title="NOTIFICATION PREFERENCES">
              {ALERT_ROWS.map(({ key, label, hint }) => (
                <Row key={key} label={label} hint={hint}>
                  <Toggle value={!!alerts[key]} onChange={v => setAlert(key, v)} />
                </Row>
              ))}
            </Section>
          </div>
        )}

        {/* ACCOUNT TAB */}
        {activeTab === "account" && (
          <div>
            <Section title="PROFILE">
              <Row label="Display Name" hint="Shown in the app header">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && saveDisplayName()}
                    className="input-dark"
                    style={{ width: 130, fontSize: 11 }}
                    placeholder="Your name"
                    maxLength={40}
                  />
                  <button
                    onClick={saveDisplayName}
                    className="btn btn-primary"
                    style={{ fontSize: 10, padding: "4px 10px" }}
                  >
                    SAVE
                  </button>
                </div>
              </Row>
              {nameSaved  && <div style={{ color: "var(--accent)",   fontSize: 10, textAlign: "right" }}>✓ Saved</div>}
              {nameError  && <div style={{ color: "var(--negative)", fontSize: 10, textAlign: "right" }}>{nameError}</div>}
              {user?.email ? (
                <Row label="Email" hint="Signed in with Supabase">
                  <span style={{
                    color: "var(--text-primary)", fontSize: 10,
                    maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {user.email}
                  </span>
                </Row>
              ) : (
                <Row label="User ID" hint="Anonymous local identifier">
                  <span style={{
                    color: "var(--text-tertiary)", fontSize: 10,
                    maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {userId.slice(0, 20)}…
                  </span>
                </Row>
              )}
              {tier && (
                <Row label="Plan" hint="Your current subscription tier">
                  <span style={{
                    color: tier === "pro" || tier === "institutional" ? "var(--accent)" : "var(--text-secondary)",
                    fontSize: 10, fontWeight: 600, letterSpacing: "0.08em",
                  }}>
                    {tier.toUpperCase()}
                  </span>
                </Row>
              )}
              <Row label="Portfolio Holdings" hint="Stocks currently in portfolio">
                <span style={{ color: "var(--pro)", fontSize: 12, fontWeight: 600 }}>{holdingsCount}</span>
              </Row>
            </Section>

            <Section title="SESSION">
              <Row label="Delete Account" hint="Permanently cancel subscription and erase all data">
                <button
                  onClick={() => setShowDeleteModal(true)}
                  style={{
                    background: "var(--surface-2)", border: "1px solid var(--negative-bg)",
                    borderRadius: "var(--radius-sm)", color: "var(--negative)",
                    fontSize: 10, padding: "5px 14px", cursor: "pointer",
                    fontFamily: "inherit", letterSpacing: "0.06em",
                  }}
                >
                  ✕ DELETE ACCOUNT
                </button>
              </Row>
            </Section>

            {showDeleteModal && (
              <DeleteAccountModal onClose={() => setShowDeleteModal(false)} />
            )}
          </div>
        )}
      </div>

      {/* ── Sticky footer ────────────────────────────────── */}
      <div style={{
        borderTop: "1px solid var(--border)",
        height: 48, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <button
            onClick={resetDefaults}
            style={{
              background: "transparent", border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)", color: "var(--text-secondary)",
              fontSize: 10, padding: "5px 14px", cursor: "pointer",
              fontFamily: "inherit", letterSpacing: "0.06em",
            }}
          >
            ↺ RESET DEFAULTS
          </button>
          <span style={{ color: "var(--text-tertiary)", fontSize: 10 }}>NUVPORT OS v3.0.0</span>
        </div>
        <a
          href="mailto:nuvportos@gmail.com"
          style={{ color: "var(--text-secondary)", fontSize: 10, textDecoration: "none", letterSpacing: "0.05em" }}
        >
          ↗ SUPPORT
        </a>
      </div>
    </div>
  );
}

